// PWA helpers — install prompt capture, platform detection, install event tracking.
//
// Usage:
//   const { isInstallable, isStandalone, platform, promptInstall, dismiss } = usePWA();
//
// Tracking is fire-and-forget to /api/web/install-event.

import { useEffect, useState, useCallback } from "react";
import api from "./api";

const DISMISS_KEY = "gully_install_dismissed_at";
const DISMISS_DAYS = 7;

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function isRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export function trackInstallEvent(sessionId, event, platform) {
  if (!sessionId) return;
  try {
    api.post("/web/install-event", { session_id: sessionId, event, platform })
       .catch(() => {});
  } catch { /* noop */ }
}

export function usePWA(sessionId) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode());
  const [platform] = useState(detectPlatform());
  const [dismissed, setDismissed] = useState(isRecentlyDismissed());

  useEffect(() => {
    function onBeforeInstallPrompt(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    function onAppInstalled() {
      setIsStandalone(true);
      setDeferredPrompt(null);
      trackInstallEvent(sessionId, "installed", platform);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [sessionId, platform]);

  const promptInstall = useCallback(async () => {
    trackInstallEvent(sessionId, "prompt_shown", platform);
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        if (outcome === "dismissed") trackInstallEvent(sessionId, "prompt_dismissed", platform);
      } catch {
        /* noop */
      }
      return { handled: true };
    }
    // iOS — no programmatic prompt; we just emit the "ios_instructions_shown" signal.
    if (platform === "ios") {
      trackInstallEvent(sessionId, "ios_instructions_shown", platform);
      return { handled: false, showIOSInstructions: true };
    }
    return { handled: false };
  }, [deferredPrompt, platform, sessionId]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
    trackInstallEvent(sessionId, "prompt_dismissed", platform);
  }, [platform, sessionId]);

  // Show banner if: not already in standalone, not dismissed recently, and
  // either (a) we have a deferredPrompt (Android/Chrome desktop) OR (b) iOS Safari.
  const isInstallable = !isStandalone && !dismissed && (Boolean(deferredPrompt) || platform === "ios");

  return { isInstallable, isStandalone, platform, promptInstall, dismiss };
}
